import React, { useState, useEffect, useCallback, useRef } from 'react';
import { FaClipboard, FaCheck } from 'react-icons/fa';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import 'katex/dist/katex.min.css';
import { generateNotesForSession, fetchLatestNotes, isNotesRequestInFlight } from '../../services/notesService';
import useLimits from '../../hooks/useLimits';

const NotesSection = ({ title, contentId, content }) => {
  const [copied, setCopied] = useState(false);
  const [notes, setNotes] = useState(null);
  const [isGenerating, setIsGenerating] = useState(true);
  const [generationError, setGenerationError] = useState(null);
  const containerRef = useRef(null);

  // useLimits for notes
  const { notes: notesLimit } = useLimits();

  // Check Firestore first, then generate if needed
  const generateNotes = useCallback(async (forceRegenerate = false) => {
    // If a request is already in-flight for this content, skip
    if (contentId && isNotesRequestInFlight(contentId)) return;

    // block if limit used up (unless forced)
    if (notesLimit !== undefined && notesLimit <= 0 && !forceRegenerate) {
      setGenerationError("You have reached your daily notes generation limit.");
      setIsGenerating(false);
      return;
    }

    setIsGenerating(true);
    setGenerationError(null);

    try {
      let notesObj = null;

      // If we have a contentId and not forcing regenerate, try fetching latest from Firestore
      if (contentId && !forceRegenerate) {
        try {
          const latest = await fetchLatestNotes(contentId);
          if (latest && latest.notesObj) {
            setNotes(latest.notesObj);
            setIsGenerating(false);
            return;
          }
        } catch (e) {
        }
      }
        notesObj = await generateNotesForSession(contentId, title);
      setNotes(notesObj);

    } catch (error) {
      setGenerationError("Failed to generate notes. Please try again.");
    } finally {
      setIsGenerating(false);
    }
  }, [title, contentId, notesLimit]);

  useEffect(() => {
    (async () => {
      if (!contentId) return;

      // Skip if a request is already in-flight (handles StrictMode double-invoke)
      if (isNotesRequestInFlight(contentId)) return;

      try {
        const latest = await fetchLatestNotes(contentId);
        if (latest && latest.notesObj) {
          setNotes(latest.notesObj);
          setIsGenerating(false);
          return;
        }
      } catch (e) {}

      // fallback to generate
      await generateNotes();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contentId]);

  const copyToClipboard = () => {
    if (!notes?.notes) return;
    navigator.clipboard.writeText(notes.notes);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div
      ref={containerRef}
      className="relative flex flex-col bg-white mb-10"
      style={{ height: "730px", overflowY: "auto", padding: "24px" }}
    >
      <div className="flex-grow">
        <div className="transition-all duration-500 opacity-100 transform-none">
          {/* Notes Header */}
          <div className="border-b border-gray-200 flex flex-wrap sm:flex-nowrap justify-between items-center gap-2">
            <div>
              <div className="flex flex-wrap mt-1 text-xs text-gray-500 gap-1">
                <span className="bg-blue-100 text-blue-800 px-4 py-0.5 rounded-full">
                  AI Generated
                </span>
              </div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => generateNotes(true)}
                disabled={isGenerating}
                className={`p-2 ${isGenerating ? 'text-gray-400' : 'text-gray-600 hover:text-blue-700 hover:bg-blue-50'} rounded-md transition-colors flex items-center text-xs sm:text-sm whitespace-nowrap`}
              >
                {isGenerating ? "Generating..." : "Regenerate"}
              </button>
              <button
                onClick={copyToClipboard}
                disabled={isGenerating || !notes}
                className={`p-2 ${isGenerating ? 'text-gray-400' : 'text-gray-600 hover:text-blue-700 hover:bg-blue-50'} rounded-md transition-colors flex items-center text-xs sm:text-sm whitespace-nowrap`}
                title="Copy to clipboard"
              >
                {copied ? <FaCheck className="text-green-500 mr-1" /> : <FaClipboard className="mr-1" />}
                <span>{copied ? 'Copied!' : 'Copy Notes'}</span>
              </button>
            </div>
          </div>

          {/* Notes Content */}
          <div id="notes-content" className="prose max-w-none notes-container">
            {isGenerating ? (
              <div className="flex items-center justify-center h-48">
                <div className="animate-pulse flex flex-col items-center">
                  <div className="h-8 bg-gray-200 rounded w-3/4 mb-4"></div>
                  <div className="h-4 bg-gray-200 rounded w-full mb-2"></div>
                  <div className="h-4 bg-gray-200 rounded w-5/6 mb-2"></div>
                  <div className="h-4 bg-gray-200 rounded w-4/6"></div>
                </div>
              </div>
            ) : generationError ? (
              <div className="text-red-500 text-center p-4">
                {generationError}
                <button 
                  onClick={() => generateNotes(true)} 
                  className="block mx-auto mt-4 px-4 py-2 bg-blue-100 text-blue-700 rounded-md hover:bg-blue-200"
                >
                  Try Again
                </button>
              </div>
            ) : (
              <ReactMarkdown
                rehypePlugins={[rehypeRaw, rehypeKatex]}
                remarkPlugins={[remarkGfm, remarkMath]}
              >
                {notes?.notes}
              </ReactMarkdown>
            )}
            <p className="text-xs text-gray-500 text-center mt-2">AI-generated responses may not always be accurate. Verify important information.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotesSection;